import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle } from '@headlessui/react'
import { TriangleAlertIcon as ExclamationTriangleIcon,
} from 'lucide-react'

/**
 * Props for the LogoutConfirmDialog component
 */
type LogoutConfirmDialogProps = {
    open: boolean
    onConfirm: () => void
    onClose: () => void
    title?: string
    description?: string
}

/**
 * LogoutConfirmDialog - Confirmation modal for logout action
 * 
 * Uses Headless UI Dialog component to display a confirmation prompt before logging out.
 * Features a warning icon, customizable title/description, and cancel/confirm buttons.
 * 
 * @param props - Component props
 * @returns A modal dialog with logout confirmation UI
 * 
 * @example
 * ```tsx
 * <LogoutConfirmDialog
 *   open={isOpen}
 *   onConfirm={handleLogout}
 *   onClose={() => setIsOpen(false)}
 *   title="Conferma logout"
 *   description="Sei sicuro di voler uscire?"
 * />
 * ```
 */
export default function LogoutConfirmDialog({
  open,
  onConfirm,
  onClose,
  title = 'Conferma logout',
  description = 'Sei sicuro di voler uscire?',
}: LogoutConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
            
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold text-slate-900">
                {title}
              </DialogTitle>
              
              <Description className="mt-2 text-sm text-slate-600">
                {description}
              </Description>
              
              <div className="mt-6 flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition"
                >
                  Annulla
                </button>
                <button
                  onClick={() => {
                    onConfirm()
                    onClose()
                  }}
                  className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition"
                >
                  Esci
                </button>
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
